<template>
  <Transition name="fade">
    <Dialog
      v-if="priceBreakdownModal"
      :title="`Order #${order_item_ref.order.order_no ? order_item_ref.order.order_no : 'N/A'} ${order_item_ref.order.order_type_detail ? order_item_ref.order.order_type_detail : ''} Price Breakdown`"
      :showCloseButton="true"
      :showFooter="true"
      :showSaveButton="false"
      :showCancelButton="false"
      :custom-footer="true"
      :lg="true"
      @closeEvent="closeEvent"
    >
      <template #modal-content>
        <template
          v-for="(factory_product, factoryProductIndex) in factory_products"
          :key="`factory_product_${factoryProductIndex}`"
        >
          <div class="flex justify-between gap-10 mb-5">
            <div class="flex items-center gap-2 mt-2 h-max">
              <img
                :src="getFilePreview(factory_product)"
                alt="Sku preview"
                class="w-20 border-0 img-thumbnail img-fluid"
                v-img-fallback
              />
              <div class="flex flex-col">
                <input
                  class="px-4 py-1 mr-2 border border-gray-300 rounded-md"
                  v-model="factory_product.product_name"
                />
                <span class="text-black-50"
                  >SKU: {{ factory_product.sku_number }}</span
                >
                <button
                  v-if="!factory_product.id"
                  @click="factory_products.splice(factoryProductIndex, 1)"
                  class="flex p-1 text-red-500 transition-all border border-red-500 rounded-md cursor-pointer gap-x-1 w-max hover:bg-red-500 hover:text-white"
                >
                  <trash-icon class="size-5" />
                  Delete
                </button>
              </div>
            </div>
            <div class="flex flex-1 gap-10 max-w-[70%]">
              <div class="flex-1">
                <table class="w-full products-table">
                  <thead v-if="factoryProductIndex === 0">
                    <tr class="text-center">
                      <th class="px-4 py-2 font-semibold text-left">Size</th>
                      <th class="px-4 py-2 font-semibold">Quantity</th>
                      <th class="px-4 py-2 font-semibold">Price</th>
                      <th class="px-4 py-2 font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      class="text-center"
                      v-for="(size, size_idx) in factory_product.prices.sizes"
                      :key="size_idx"
                    >
                      <td class="p-1 w-[25%] text-left">{{ size.size }}</td>
                      <td class="p-1 w-[25%]">{{ size.quantity }}</td>
                      <td class="p-1 w-[25%]">
                        <span
                          ><input
                            type="number"
                            class="w-20 p-1 mr-2 border border-gray-300 rounded-md outline-none"
                            v-model="size.final_net_price"
                            @change="
                              () => {
                                size.final_net_price = size.final_net_price
                                  ? parseFloat(size.final_net_price).toFixed(2)
                                  : 0;
                                handlePriceUpdate(size, size.quantity);
                              }
                            "
                          />{{
                            order_item_ref?.order?.additional_fields?.currency
                              ?.msrp?.code ?? ""
                          }}</span
                        >
                      </td>
                      <td class="p-1 w-[25%] text-right">
                        {{ formatNumber(size.final_net_price_with_quantity) }}
                        <strong>{{
                          order_item_ref?.order?.additional_fields?.currency
                            ?.msrp?.code ?? ""
                        }}</strong>
                      </td>
                    </tr>
                    <tr v-if="factory_product.prices.total_addons > 0">
                      <td colspan="4">
                        <h1
                          class="p-1 mb-1 font-semibold text-center border text-md border-grey-300"
                        >
                          Addons
                        </h1>
                        <table class="w-full mt-4">
                          <tbody>
                            <!-- simple addons starts -->
                            <template
                              v-for="(addon, addon_idx) in factory_product
                                .prices.addons"
                              :key="addon_idx"
                            >
                              <tr class="text-center">
                                <td class="p-1 w-[25%] text-left">
                                  {{ addon.addon_name }}
                                </td>
                                <td class="p-1 w-[25%]"></td>
                                <td class="p-1 w-[25%]">
                                  <input
                                    type="number"
                                    class="w-20 p-1 mr-2 border border-gray-300 rounded-md outline-none"
                                    v-model="addon.final_net_price"
                                    @change="
                                      () => {
                                        addon.final_net_price =
                                          addon.final_net_price
                                            ? parseFloat(
                                                addon.final_net_price,
                                              ).toFixed(2)
                                            : 0;
                                        handlePriceUpdate(
                                          addon,
                                          factory_product.prices.total_quantity,
                                        );
                                      }
                                    "
                                  />
                                  {{
                                    order_item_ref?.order?.additional_fields
                                      ?.currency?.msrp?.code ?? ""
                                  }}
                                </td>
                                <td class="p-1 w-[25%] text-right">
                                  {{
                                    formatNumber(
                                      addon.final_net_price_with_quantity,
                                    )
                                  }}
                                  <strong>{{
                                    order_item_ref?.order?.additional_fields
                                      ?.currency?.msrp?.code ?? ""
                                  }}</strong>
                                </td>
                              </tr>
                            </template>
                            <!-- simple addons ends -->
                            <!-- ungrouped addons starts -->
                            <template
                              v-for="(
                                ungrouped_addon, ungrouped_addon_idx
                              ) in factory_product.prices.ungrouped_addons"
                              :key="`ungrouped_addon_${ungrouped_addon_idx}`"
                            >
                              <tr class="text-center">
                                <td class="p-1 w-[25%] text-left">
                                  {{ ungrouped_addon.addon_name }}
                                </td>
                                <td class="p-1 w-[25%]"></td>
                                <td class="p-1 w-[25%]">
                                  <input
                                    type="number"
                                    class="w-20 p-1 mr-2 border border-gray-300 rounded-md outline-none"
                                    v-model="ungrouped_addon.final_net_price"
                                    @change="
                                      () => {
                                        ungrouped_addon.final_net_price =
                                          ungrouped_addon.final_net_price
                                            ? parseFloat(
                                                ungrouped_addon.final_net_price,
                                              )
                                            : 0;
                                        handlePriceUpdate(
                                          ungrouped_addon,
                                          factory_product.prices.total_quantity,
                                        );
                                      }
                                    "
                                  />
                                  {{
                                    order_item_ref?.order?.additional_fields
                                      ?.currency?.msrp?.code ?? ""
                                  }}
                                </td>
                                <td class="p-1 w-[25%] text-right">
                                  {{
                                    formatNumber(
                                      ungrouped_addon.final_net_price_with_quantity,
                                    )
                                  }}
                                  <strong>{{
                                    order_item_ref?.order?.additional_fields
                                      ?.currency?.msrp?.code ?? ""
                                  }}</strong>
                                </td>
                              </tr>
                            </template>
                            <!-- ungrouped addons ends -->
                            <!-- grouped addons starts -->
                            <template
                              v-for="(
                                grouped_addon, group_name
                              ) in factory_product.prices.grouped_addons"
                              :key="`grouped_${group_name}_selected_addon`"
                            >
                              <tr class="text-center">
                                <td class="p-1 w-[25%] text-left">
                                  {{ group_name }}:
                                  {{ grouped_addon.addon_name }}
                                </td>
                                <td class="p-1 w-[25%]"></td>
                                <td class="p-1 w-[25%]">
                                  <input
                                    type="number"
                                    class="w-20 p-1 mr-2 border border-gray-300 rounded-md outline-none"
                                    v-model="grouped_addon.final_net_price"
                                    @change="
                                      () => {
                                        grouped_addon.final_net_price =
                                          grouped_addon.final_net_price
                                            ? parseFloat(
                                                grouped_addon.final_net_price,
                                              )
                                            : 0;
                                        handlePriceUpdate(
                                          grouped_addon,
                                          factory_product.prices.total_quantity,
                                        );
                                      }
                                    "
                                  />{{
                                    order_item_ref?.order?.additional_fields
                                      ?.currency?.msrp?.code ?? ""
                                  }}
                                </td>
                                <td class="p-1 w-[25%] text-right">
                                  {{
                                    formatNumber(
                                      grouped_addon.final_net_price_with_quantity,
                                    )
                                  }}
                                  <strong>{{
                                    order_item_ref?.order?.additional_fields
                                      ?.currency?.msrp?.code ?? ""
                                  }}</strong>
                                </td>
                              </tr>
                            </template>
                            <!-- grouped addons ends -->
                            <!-- logo technology addons starts -->
                            <template
                              v-for="(
                                logo_technology, logo_technologies_index
                              ) in factory_product.prices.logo_technologies"
                              :key="`logo_technology_${logo_technologies_index}_addon`"
                            >
                              <tr class="text-center">
                                <td class="p-1 w-[25%] text-left">
                                  {{ logo_technology.addon_name }}
                                </td>
                                <td class="p-1 w-[25%]"></td>
                                <td class="p-1 w-[25%]">
                                  <input
                                    type="number"
                                    class="w-20 p-1 mr-2 border border-gray-300 rounded-md outline-none"
                                    v-model="logo_technology.final_net_price"
                                    @change="
                                      () => {
                                        logo_technology.final_net_price =
                                          logo_technology.final_net_price
                                            ? parseFloat(
                                                logo_technology.final_net_price,
                                              )
                                            : 0;
                                        handlePriceUpdate(
                                          logo_technology,
                                          factory_product.prices.total_quantity,
                                        );
                                      }
                                    "
                                  />{{
                                    order_item_ref?.order?.additional_fields
                                      ?.currency?.msrp?.code ?? ""
                                  }}
                                </td>
                                <td class="p-1 w-[25%] text-right">
                                  {{
                                    formatNumber(
                                      logo_technology.final_net_price_with_quantity,
                                    )
                                  }}
                                  <strong>{{
                                    order_item_ref?.order?.additional_fields
                                      ?.currency?.msrp?.code ?? ""
                                  }}</strong>
                                </td>
                              </tr>
                            </template>
                            <!-- logo technology addons ends -->
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <hr class="mt-1 border border-gray-300" />
                <table class="w-full">
                  <tbody>
                    <tr class="text-center">
                      <td class="p-1 w-[25%]"></td>
                      <td class="p-1 w-[25%]">
                        Qty:
                        <strong>{{
                          factory_product.prices.total_quantity
                        }}</strong>
                      </td>
                      <td class="p-1 w-[25%]"></td>
                      <td class="p-1 w-[25%] text-right">
                        <strong>Total:</strong>
                        {{
                          formatNumber(
                            factory_product.prices.factory_product_total_price,
                          )
                        }}
                        <strong>{{
                          order_item_ref?.order?.additional_fields?.currency
                            ?.msrp?.code ?? ""
                        }}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
        <hr />
        <table class="w-full">
          <tbody>
            <tr>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td
                class="p-1 w-[25%]"
                style="text-align: left; padding-left: 4.5%"
              >
                Total Qty:
                <strong>{{ order_item_ref.price_info.total_quantity }}</strong>
              </td>
              <td class="p-1 w-[25%] text-right">
                <strong>Total Price:</strong>
                {{ formatNumber(order_item_ref.price_info.total_price) }}
                <strong>{{
                  order_item_ref?.order?.additional_fields?.currency?.msrp
                    ?.code ?? ""
                }}</strong>
              </td>
            </tr>
            <tr class="text-right">
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]">
                <strong>Shipping Cost:</strong>
                {{ formatNumber(order_item_ref.price_info.shipping) }}
                <strong>{{
                  order_item_ref?.order?.additional_fields?.currency?.msrp
                    ?.code ?? ""
                }}</strong>
              </td>
            </tr>
            <tr class="text-right">
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>

              <td class="p-1 w-[25%]" v-if="order_item_ref.price_info.is_fixed">
                <strong>Discount:</strong>
                {{
                  formatNumber(
                    order_item_ref.price_info.fixed_value
                      ? order_item_ref.price_info.fixed_value
                      : 0,
                  )
                }}
                <strong>{{
                  order_item_ref?.order?.additional_fields?.currency?.msrp
                    ?.code ?? ""
                }}</strong>
              </td>
              <td class="p-1 w-[25%]" v-else>
                <strong>Discount:</strong> {{ formatNumber(discount_amount) }}
                <strong>{{
                  order_item_ref?.order?.additional_fields?.currency?.msrp
                    ?.code ?? ""
                }}</strong>
              </td>
            </tr>
            <tr class="text-right">
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]">
                <strong>Final Price:</strong>
                {{
                  formatNumber(order_item_ref.price_info.order_item_final_price)
                }}
                <strong>{{
                  order_item_ref?.order?.additional_fields?.currency?.msrp
                    ?.code ?? ""
                }}</strong>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="flex gap-x-2 mt-5">
          <button
            class="flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-md cursor-pointer hover:text-white hover:bg-[#219F84] duration-200"
            @click="addProductModal = true"
          >
            <plus-icon class="size-5"></plus-icon> Add Item
          </button>
          <template v-if="can('update-techpack')">
            <a
              :href="`${appUrl}/edit-techpack/${(order_item as DashboardItem).order?.id}?source=new_dashboard`"
              class="border p-2 rounded-md border-[#219F84] text-[#219F84]"
            >
              Edit Tech Pack
            </a>
          </template>
        </div>
      </template>
      <template #footer>
        <div class="flex w-full justify-between items-end">
          <div class="flex gap-x-5 items-end">
            <button
              @click="shippingAndDiscountModal = true"
              class="border p-2 rounded-md border-[#219F84] text-[#219F84] h-max"
            >
              Shipping and discount
            </button>

            <div class="flex items-start gap-1 flex-col">
              <label for="orderType" class="text-[14px]">Order Type:</label>
              <Select
                append-to-body
                v-model="selectedOrderType"
                :options="orderTypes"
                label="label"
                placeholder="Select Order Type"
                :clearable="false"
                @option:selected="handleOrderTypeUpdate"
                :class="{ 'border-red-500': errorMessages['order_type'] }"
                class="min-w-[300px] w-fit"
              />
            </div>
          </div>

          <div class="flex gap-x-5">
            <button
              class="border p-2 rounded-md bg-[#219F84] text-white h-max"
              @click="saveOrderItem"
            >
              Save
            </button>
          </div>
        </div>
      </template>
    </Dialog>
  </Transition>
  <Transition name="fade">
    <AddProductModal
      v-if="addProductModal"
      @on-close="() => (addProductModal = false)"
      :customerId="(order_item as DashboardItem).order.customer_id"
      :companyId="(order_item as DashboardItem).order.company_id"
      :orderItem="order_item"
      :company_products="company_products"
      @add-factory-product="addFactoryProduct"
    />
  </Transition>
  <Transition name="fade">
    <Dialog
      v-if="shippingAndDiscountModal"
      title="Add Shipping and Discount"
      :showCloseButton="true"
      :showFooter="true"
      @close-event="
        discount_shipping_info = JSON.parse(
          JSON.stringify(order_item_ref.price_info),
        )
      "
      @cancel-event="
        discount_shipping_info = JSON.parse(
          JSON.stringify(order_item_ref.price_info),
        )
      "
      :showSaveButton="false"
      :showCancelButton="false"
      :custom-footer="true"
      :sm="true"
      @closeEvent="closeShippingAndDiscountModal"
    >
      <template #modal-content>
        <div>
          <div class="flex justify-between">
            <label class="block mb-1 text-sm font-medium text-gray-700"
              >Add Discount</label
            >
            <div class="flex items-center gap-x-2">
              <input
                type="checkbox"
                name="Fixed Discount"
                class="w-4 h-4"
                tr
                v-model="discount_shipping_info.is_fixed"
              />
              <label for="Fixed Discount">Fixed Price</label>
            </div>
          </div>
          <template v-if="discount_shipping_info.is_fixed">
            <input
              class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
              type="number"
              v-model="discount_shipping_info.fixed_value"
              @change="
                () => {
                  discount_shipping_info.fixed_value =
                    discount_shipping_info.fixed_value
                      ? parseFloat(discount_shipping_info.fixed_value).toFixed(
                          2,
                        )
                      : parseFloat('0').toFixed(2);
                }
              "
              placeholder="Enter Fixed Price"
            />
          </template>
          <template v-else>
            <div class="relative">
              <input
                class="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md outline-none"
                type="number"
                v-model="discount_shipping_info.discount"
                @change="
                  () => {
                    discount_shipping_info.discount =
                      discount_shipping_info.discount
                        ? parseFloat(discount_shipping_info.discount).toFixed(2)
                        : parseFloat('0').toFixed(2);
                  }
                "
                placeholder="Enter Discount Percentage"
              />
              <span class="absolute transform -translate-y-1/2 right-3 top-1/2"
                >%</span
              >
            </div>
          </template>
          <label class="block mt-4 mb-1 text-sm font-medium text-gray-700"
            >Add Shipping</label
          >
          <input
            class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
            type="number"
            v-model="discount_shipping_info.shipping"
            @change="
              () => {
                discount_shipping_info.shipping =
                  discount_shipping_info.shipping
                    ? parseFloat(discount_shipping_info.shipping).toFixed(2)
                    : parseFloat('0').toFixed(2);
              }
            "
            placeholder="Enter Shipping Cost"
          />
          <span
            class="absolute transform -translate-y-1/2 right-6"
            style="top: 65%"
            >{{
              order_item_ref?.order?.additional_fields?.currency?.msrp?.code ??
              ""
            }}</span
          >
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end w-full">
          <button
            @click="calculateShippingAndDiscount"
            class="border px-4 py-2 rounded-md border-[#219F84] text-[#219F84]"
          >
            Save
          </button>
        </div>
      </template>
    </Dialog>
  </Transition>
</template>
<script setup lang="ts">
import Dialog from "@/components/Shared/Dialog.vue";
import Select from "@/components/Shared/Select.vue";
import { showToast } from "@/composables/swal";
import {
  handleResponseException,
  http,
} from "@/interceptors/axiosRequestInterceptor";
import {
  ApiResponse2,
  DashboardItem,
  OrderType,
  Product,
  Result,
} from "@/models";
import { useMainStore } from "@/store";
import { orderTypes } from "@/utils/constants/orderTypes";
import { PlusIcon, TrashIcon } from "@heroicons/vue/24/outline";
import { storeToRefs } from "pinia";
import { computed, onMounted, reactive, ref, toRef } from "vue";
import AddProductModal from "./AddProductModal.vue";
const { loading } = storeToRefs(useMainStore());

const props = defineProps<{
  order_item: any;
  can: any;
  appUrl: string;
}>();

const order_item_ref = ref(JSON.parse(JSON.stringify(props.order_item)));
let original_factory_products = JSON.parse(
  JSON.stringify(order_item_ref.value.factory_products),
);
let original_order_item_price_info = JSON.parse(
  JSON.stringify(order_item_ref.value.price_info),
);
const factory_products = ref(order_item_ref.value.factory_products ?? []);
const addFactoryProduct = (payload: any) => {
  factory_products.value = [...factory_products.value, payload.factory_product];
  calculateTotalPrice();
};

const selectedOrderType = ref(null);

function handleOrderTypeUpdate(order_type: OrderType) {
  selectedOrderType.value = order_type;
}
const errorMessages = reactive<Record<string, string>>({});

//order discount and shipping starts
const discount_shipping_info = ref<any>({
  is_fixed: false,
  fixed_value: 0.0,
  discount: 0.0,
  shipping: 0.0,
  total_price: 0,
  total_quantity: 0,
  order_item_final_price: 0,
});

const discount_amount = computed(() => {
  const discount = order_item_ref.value.price_info.discount
    ? parseFloat(order_item_ref.value.price_info.discount)
    : 0;
  return (order_item_ref.value.price_info.total_price * discount) / 100;
});
//order discount and shipping ends

const emit = defineEmits(["close"]);
// const { getMerchantProducts } = useMainStore();
const priceBreakdownModal = ref<boolean>(false);
const shippingAndDiscountModal = ref<boolean>(false);
const addProductModal = ref<boolean>(false);
onMounted(() => {
  priceBreakdownModal.value = true;
  getMerchantProducts((order_item_ref.value as DashboardItem).order.company_id);
  discount_shipping_info.value = JSON.parse(
    JSON.stringify(order_item_ref.value.price_info),
  );
  selectedOrderType.value = {
    label: order_item_ref.value.order.order_type_detail || "",
    symbol: order_item_ref.value.order.order_type_symbol || "",
    default: order_item_ref.value.order.order_type_default || false,
  };
});

let company_products = ref<Product[]>([]);

const getMerchantProducts = async (company_id: number) => {
  http
    .get<
      ApiResponse2<Result<Product[]>>
    >(`merchant_products?company_id=${company_id}`)
    .then((successResponse: any) => {
      company_products = successResponse.data.result.products;
    });
};

const closeEvent = async () => {
  factory_products.value = original_factory_products;
  order_item_ref.value.price_info = original_order_item_price_info;
  emit("close");
  priceBreakdownModal.value = false;
};

const closeShippingAndDiscountModal = () => {
  shippingAndDiscountModal.value = false;
};

const storage_url = import.meta.env.VITE_APP_FILE_URL;

const handlePriceUpdate = (item: any, quantity: any) => {
  item.final_net_price = parseFloat(item.final_net_price).toFixed(2);
  const total = quantity * item.final_net_price;
  item.final_net_price_with_quantity = total.toFixed(2);
  calculateTotalPrice();
};

const getFilePreview = (factory_product: any) => {
  if (factory_product.front_image_file instanceof File) {
    return factory_product.front_image_file
      ? URL.createObjectURL(factory_product.front_image_file)
      : "";
  } else {
    return `${storage_url}/${factory_product.front_image}`;
  }
};

const formatNumber = (value) => {
  // Ensure the value is a number and format it to 2 decimal places
  const num = parseFloat(value) || 0;
  return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const calculateTotalPrice = () => {
  let order_item_total_price = 0;
  let order_item_total_quantity = 0;
  factory_products.value.forEach((factory_product) => {
    order_item_total_quantity =
      order_item_total_quantity +
      parseInt(factory_product.prices.total_quantity);
    let total = 0;
    factory_product.prices.sizes.forEach((size: any) => {
      total += parseFloat(size.final_net_price_with_quantity);
    });
    factory_product.prices.addons.forEach((addon: any) => {
      total += parseFloat(addon.final_net_price_with_quantity);
    });
    factory_product.prices.ungrouped_addons.forEach((ungrouped_addon: any) => {
      total += parseFloat(ungrouped_addon.final_net_price_with_quantity);
    });
    Object.values(factory_product.prices.grouped_addons).forEach(
      (grouped_addon: any) => {
        total += parseFloat(grouped_addon.final_net_price_with_quantity);
      },
    );
    factory_product.prices.logo_technologies.forEach((logo_technology: any) => {
      total += parseFloat(logo_technology.final_net_price_with_quantity);
    });
    factory_product.prices.factory_product_total_price = total;
    order_item_total_price = order_item_total_price + total;
  });
  order_item_ref.value.price_info.total_price = order_item_total_price;
  order_item_ref.value.price_info.total_quantity = order_item_total_quantity;
  order_item_ref.value.price_info.order_item_final_price =
    order_item_total_price;
  calculateShippingAndDiscount();
};

const calculateShippingAndDiscount = () => {
  const order_item_total_price = parseFloat(
    order_item_ref.value.price_info.total_price,
  );
  let order_item_final_price = parseFloat(
    order_item_ref.value.price_info.order_item_final_price,
  );
  discount_shipping_info.value.fixed_value = discount_shipping_info.value
    .fixed_value
    ? parseFloat(discount_shipping_info.value.fixed_value)
    : 0;
  discount_shipping_info.value.discount = discount_shipping_info.value.discount
    ? parseFloat(discount_shipping_info.value.discount)
    : 0;
  discount_shipping_info.value.shipping = discount_shipping_info.value.shipping
    ? parseFloat(discount_shipping_info.value.shipping)
    : 0;
  order_item_ref.value.price_info.is_fixed =
    discount_shipping_info.value.is_fixed;
  order_item_ref.value.price_info.fixed_value =
    discount_shipping_info.value.fixed_value;
  order_item_ref.value.price_info.discount =
    discount_shipping_info.value.discount;
  order_item_ref.value.price_info.shipping =
    discount_shipping_info.value.shipping;
  if (discount_shipping_info.value.is_fixed) {
    order_item_final_price =
      order_item_total_price -
      (discount_shipping_info.value.fixed_value
        ? discount_shipping_info.value.fixed_value
        : 0);
  } else {
    const discount = discount_shipping_info.value.discount
      ? discount_shipping_info.value.discount
      : 0;
    const discount_amount = (order_item_total_price * discount) / 100;
    order_item_final_price = order_item_total_price - discount_amount;
  }
  const shipping = discount_shipping_info.value.shipping
    ? discount_shipping_info.value.shipping
    : 0;
  order_item_final_price = order_item_final_price + shipping;
  order_item_ref.value.price_info.order_item_final_price =
    order_item_final_price.toFixed(2);
  closeShippingAndDiscountModal();
};

const saveOrderItem = async () => {
  loading.value = true;
  await uploadFactoryProductsFiles();
  const order_item = order_item_ref.value;
  order_item.factory_products = factory_products.value;
  order_item.order.order_type_detail = selectedOrderType.value?.label || "";
  order_item.order.order_type_symbol = selectedOrderType.value?.symbol || "";

  http
    .post(`update/order/price`, order_item)
    .then((response: any) => {
      loading.value = false;
      showToast(response.data.message);
      emit("close", true);
      priceBreakdownModal.value = false;
    })
    .catch((err) => {
      loading.value = false;
      handleResponseException(err);
    });
};

const uploadFactoryProductsFiles = async () => {
  let base_path = `comapny_${order_item_ref.value.order.company_id}/${order_item_ref.value.order.company_id}/order/${order_item_ref.value.order.customer_id}/{order_item_ref.value.order.id}`;
  if (order_item_ref.value.factory_id) {
    base_path = `${base_path}/${order_item_ref.value.factory_id}`;
  } else {
    base_path = `${base_path}/shareable`;
  }
  const factory_product_files = factory_products.value
    .map(async (factory_product: any, factory_product_index: number) => {
      const factory_product_form_data = new FormData();
      if (factory_product.front_image_file instanceof File) {
        factory_product_form_data.append(
          "front_image",
          factory_product.front_image_file,
        );
      }
      if (factory_product.back_image_file instanceof File) {
        factory_product_form_data.append(
          "back_image",
          factory_product.back_image_file,
        );
      }
      if (Array.isArray(factory_product.assets)) {
        factory_product.assets.forEach((asset, asset_index) => {
          if (asset instanceof File) {
            factory_product_form_data.append(`assets[${asset_index}]`, asset);
          }
        });
      }
      if (
        factory_product_form_data.has("front_image") ||
        factory_product_form_data.has("back_image") ||
        factory_product_form_data.has("assets[0]")
      ) {
        factory_product_form_data.append("base_path", base_path);
        const factory_producty_response = await http.post(
          "upload/price/modal/files",
          factory_product_form_data,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );

        return {
          status: factory_producty_response.status,
          factory_product_index,
          data: factory_producty_response.data.result,
        };
      } else {
        return null;
      }
    })
    .filter(Boolean);
  const results = await Promise.allSettled(factory_product_files);
  results.forEach((result: any) => {
    if (result.status === "fulfilled" && result.value) {
      const { factory_product_index, data } = result.value;
      factory_products.value[factory_product_index].front_image =
        data.front_image;
      factory_products.value[factory_product_index].back_image =
        data.back_image;
      factory_products.value[factory_product_index].assets = data.assets;
      delete factory_products.value[factory_product_index].front_image_file;
      delete factory_products.value[factory_product_index].back_image_file;
    }
  });
};
</script>

<style lang="scss">
.products-table {
  th,
  td {
    border: 1px solid #e5e7eb;
    padding: 5px;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: white;
  }

  th {
    position: sticky;
    top: 0;
    background-color: white;
    z-index: 10;
  }
}

.modal-body {
  padding-top: 0 !important;
}
</style>
